import { useNavigate, useParams } from '@solidjs/router'
import { Check, X } from 'lucide-solid'
import { Component, createEffect, createMemo, createSignal, JSX, on, onMount, Show } from 'solid-js'
import Button from '../../shared/Button'
import Select from '../../shared/Select'
import PersonaAttributes, { fromAttrs, toAttrs } from '../../shared/PersonaAttributes'
import TextInput from '../../shared/TextInput'
import {
  characterStore,
  chatStore,
  presetStore,
  scenarioStore,
  settingStore,
  userStore,
  i18nStore,
} from '../../store'
import CharacterSelect from '../../shared/CharacterSelect'
import { AutoPreset, getPresetOptions } from '../../shared/adapter'
import { defaultPresets } from '/common/presets'
import ServiceWarning from '/web/shared/ServiceWarning'
import { PresetSelect } from '/web/shared/PresetSelect'
import { Card, TitleCard } from '/web/shared/Card'
import { Toggle } from '/web/shared/Toggle'
import Divider from '/web/shared/Divider'
import PageHeader from '/web/shared/PageHeader'
import { isLoggedIn } from '/web/store/api'
import { AppSchema } from '/common/types'
import { ADAPTER_LABELS } from '/common/adapters'
import { Page } from '/web/Layout'
import { createStore } from 'solid-js/store'
import { isDefaultPreset } from '/common/default-preset'

const options = (i18n: any) => [{ value: 'attributes', label: i18n.t.char_attributesFormat }]

type ChatState = {
  name: string
  presetId: string
  imageSource: NonNullable<AppSchema.Chat['imageSource']>
  mode: NonNullable<AppSchema.Chat['mode']>
  useOverrides: boolean
  scenarioId: string
  scenarioStates: NonNullable<AppSchema.Chat['scenarioStates']>
  greeting: string
  scenario: string
  sampleChat: string
  systemPrompt: string
  postHistoryInstructions: string
  personaKind: NonNullable<AppSchema.Persona['kind']>
  personaAttrs: Array<{ key: string; values: string }>
}

const CreateChatForm: Component<{
  footer?: (footer: JSX.Element) => void
  close?: () => void
  charId?: string
}> = (props) => {
  let ref: any
  const params = useParams()
  const nav = useNavigate()

  const scen = scenarioStore((s) => s.scenarios)
  const cfg = settingStore((s) => ({ config: s.config }))
  const user = userStore((s) => ({ ...s.user, sub: s.sub, userLevel: s.userLevel }))
  const i18n = i18nStore((s) => ({ language: s.language, t: s.t }))
  const chars = characterStore((s) => ({
    char: s.editing,
    chars: (s.characters?.list || []).filter((c) => !isLoggedIn() || c.userId === user._id),
    loaded: s.characters.loaded,
  }))

  const [state, setState] = createStore(getInitState(chars.char, { presetId: user.defaultPreset }))
  const [selectedId, setSelected] = createSignal<string | undefined>(params.id)

  const scenarios = createMemo(() => {
    if (!scen.length) return [{ value: '', label: i18n.t.char_noCharacterBook }]
    return [{ value: '', label: i18n.t.chat_none }, ...scen.map((s) => ({ label: s.name, value: s._id }))]
  })

  createEffect(
    on(
      () => [props.charId, chars.chars],
      () => {
        if (props.charId) return
        const curr = selectedId()
        if (curr) return

        if (!chars.chars.length) return
        setSelected(chars.chars[0]._id)
      }
    )
  )

  createEffect(() => {
    const id = selectedId()
    if (!id) return

    if (chars.char?._id === id && '__type' in chars.char === false) return
    characterStore.getCharacter(id)
  })

  createEffect(
    on(
      () => chars.char?._id,
      (id) => {
        if (!id) return
        const next = getInitState(chars.char, state)
        setState(next)
      }
    )
  )

  const presets = presetStore((s) => s.presets)
  const presetOptions = createMemo(() => {
    const opts = getPresetOptions(presets, { builtin: true }).filter((pre) => pre.value !== 'chat')
    const combined = [
      {
        label: i18n.t.chat_system_preset_horde,
        value: 'horde',
        custom: false,
        name: i18n.t.chat_system_preset,
        provider: 'Horde',
      },
    ].concat(opts)

    const defaultPreset = presets.find((p) => p._id === user.defaultPreset)
    if (defaultPreset) {
      const label = ADAPTER_LABELS[defaultPreset.service!]
      combined.unshift({
        label: `[${label}] ${i18n.t.chat_your_default_preset}`,
        value: '',
        custom: true,
        provider: label,
        name: i18n.t.chat_your_default_preset,
      })
    }

    return combined
  })

  const selectedPreset = createMemo(() => {
    const id = state.presetId || user.defaultPreset

    if (!id) {
      const userLevel = user.userLevel
      const eligible = cfg.config.subs.some((sub) => userLevel >= sub.level)

      if (eligible) {
        return defaultPresets.agnai
      }

      return defaultPresets.horde
    }

    if (isDefaultPreset(id)) return defaultPresets[id]
    return presets.find((pre) => pre._id === id)
  })

  const onCreate = () => {
    if (!chars.char) return

    const characterId = chars.char._id

    const overrides = state.useOverrides
      ? {
          greeting: state.greeting,
          scenario: state.scenario,
          sampleChat: state.sampleChat,
          genPreset: state.presetId,
          overrides: { kind: state.personaKind, attributes: fromAttrs(state.personaAttrs) },
        }
      : {
          greeting: undefined,
          scenario: undefined,
          sampleChat: undefined,
          overrides: undefined,
        }

    const payload = {
      name: state.name,
      schema: state.personaKind,
      mode: state.mode,
      ...overrides,
      useOverrides: state.useOverrides,
      genPreset: state.presetId,
      scenarioId: state.scenarioId,
    }
    chatStore.createChat(characterId, payload, (id) => nav(`/chat/${id}`))
  }

  const footer = (
    <>
      <Button schema="secondary" onClick={props.close}>
        <X />
        {i18n.t.close}
      </Button>

      <Button onClick={onCreate} disabled={!chars.char}>
        <Check />
        {i18n.t.chat_create_button}
      </Button>
    </>
  )

  onMount(() => {
    props.footer?.(footer)
  })

  return (
    <Page>
      <PageHeader
        title={i18n.t.chat_create_title}
        subtitle={
          <div class="flex w-full justify-between">
            <div>{chars.char?.name || ''}</div>
            <Button onClick={onCreate} size="sm">
              {i18n.t.chat_create_button}
            </Button>
          </div>
        }
      />
      <form ref={ref}>
        <div class="mb-2 text-sm">
          {i18n.t.chat_create_description}
        </div>
        <div class="mb-4 text-sm">
          {i18n.t.chat_create_applied_to_new}
        </div>
        <div class="flex flex-col gap-3">
          <Show when={!props.charId}>
            <Card>
              <CharacterSelect
                class="w-48"
                items={chars.chars}
                value={chars.char}
                fieldName="character"
                label={i18n.t.admin_model}
                helperText={i18n.t.chat_create_main_character_helper}
                onChange={(c) => setSelected(c?._id)}
                ignoreActive
                random
              />
            </Card>
          </Show>
          <Card>
            <PresetSelect
              options={presetOptions()}
              selected={state.presetId}
              setPresetId={(id) => setState('presetId', id)}
              warning={<ServiceWarning preset={selectedPreset()} />}
            />
          </Card>

          <Card>
            <Select
              label={i18n.t.chat_mode}
              helperText={
                <div class="flex flex-col gap-2">
                  <TitleCard>
                    {i18n.t.chat_mode_companion_description}
                  </TitleCard>
                </div>
              }
              items={[
                { label: i18n.t.chat_mode_standard, value: 'standard' },
                { label: i18n.t.chat_mode_companion, value: 'companion' },
              ]}
              value={'standard'}
              onChange={(ev) => setState('mode', ev.value as any)}
            />
          </Card>

          <Card>
            <TextInput
              class="text-sm"
              label={i18n.t.chat_conversation_name}
              helperText={
                <span>
                  {i18n.t.chat_conversation_name_helper}
                </span>
              }
              placeholder={i18n.t.admin_notYetImplemented}
              onChange={(ev) => setState('name', ev.currentTarget.value)}
            />
          </Card>
          <Card>
            <Toggle
              label={i18n.t.chat_override_character_definitions}
              helperText={i18n.t.chat_override_helper}
              value={state.useOverrides}
              onChange={(ev) => setState('useOverrides', ev)}
            />
          </Card>

          <Divider />

          <Select
            label={i18n.t.char_scenario}
            helperText={i18n.t.chat_scenario_helper}
            items={scenarios()}
            onChange={(ev) => setState('scenarioId', ev.value)}
            disabled={scen.length === 0}
          />

          <Card>
            <TextInput
              isMultiline
              label={i18n.t.char_greeting}
              class="text-xs"
              disabled={!state.useOverrides}
              value={state.greeting}
              onChange={(ev) => setState('greeting', ev.currentTarget.value)}
            />
          </Card>
          <Card>
            <TextInput
              isMultiline
              label={i18n.t.char_scenario}
              value={state.scenario}
              class="text-xs"
              disabled={!state.useOverrides}
              onChange={(ev) => setState('scenario', ev.currentTarget.value)}
            ></TextInput>
          </Card>

          <Card>
            <TextInput
              isMultiline
              label={i18n.t.char_sampleConversation}
              value={state.sampleChat}
              class="text-xs"
              disabled={!state.useOverrides}
              onChange={(ev) => setState('sampleChat', ev.currentTarget.value)}
            ></TextInput>
          </Card>

          <Card>
            <Show when={state.personaKind !== 'text'}>
              <Select
                class="mb-2 text-sm"
                label={i18n.t.persona}
                items={options(i18n)}
                value={state.personaKind || 'attributes'}
                disabled={!state.useOverrides}
                onChange={(ev) => setState('personaKind', ev.value as any)}
              />
            </Show>

            <Show when={state.personaKind === 'text'}>
              <Select
                class="mb-2 text-sm"
                label={i18n.t.persona}
                items={[{ label: i18n.t.char_plainTextFormat, value: 'text' }]}
                value={'text'}
                disabled={!state.useOverrides}
              />
            </Show>

            <div class="w-full text-sm">
              <Show when={chars.char}>
                <PersonaAttributes
                  state={state.personaAttrs}
                  setter={(next) => setState('personaAttrs', next)}
                  hideLabel
                  schema={state.personaKind}
                  disabled={!state.useOverrides}
                />
              </Show>
            </div>
          </Card>
        </div>

        <Show when={!props.footer}>
          <Card class="mb-8 mt-2">
            <div class="flex w-full justify-end gap-2">{footer}</div>
          </Card>
        </Show>
      </form>
    </Page>
  )
}

function getInitState(char?: AppSchema.Character, previous?: Partial<ChatState>): ChatState {
  return {
    name: previous?.name || '',
    presetId: previous?.presetId || '',
    imageSource: previous?.imageSource || 'settings',
    mode: previous?.mode || 'standard',

    useOverrides: previous?.useOverrides ?? false,
    scenarioId: previous?.scenarioId || '',
    scenarioStates: previous?.scenarioStates || [],

    greeting: char?.greeting || '',
    scenario: char?.scenario || '',
    sampleChat: char?.sampleChat || '',
    systemPrompt: '',
    postHistoryInstructions: '',

    personaKind: char?.persona?.kind || 'text',
    personaAttrs: toAttrs(char?.persona?.attributes),
  }
}

export default CreateChatForm
